/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "../../model/device";
import { Locale, LocaleKeys } from "../../model/locale";
import type { AlertOverlayAction } from "../alertOverlay";

/**
 * Buttons and strings for the root error overlay — the last-resort UI shown
 * when a screen could not be loaded or rendered and a silent reload has
 * already been tried. See {@link RootErrorBoundary}.
 *
 * The pair mirrors the connection-error overlay so the two read as one
 * experience: Reload re-fetches the app, and Exit closes it on TV and VegaOS
 * only — a browser tab cannot close the app the same way (UAW-2577).
 */

/** Action id for the Reload button. Reloads the current URL. */
export const APP_LOAD_ERROR_RELOAD_ACTION_ID: string = "app-load-error-reload";
/** Action id for the Exit button. Closes the app (TV / VegaOS only). */
export const APP_LOAD_ERROR_EXIT_ACTION_ID: string = "app-load-error-exit";

/** Title of the root error overlay. */
export function getAppLoadErrorTitle(): string {
  return Locale.getString(LocaleKeys.ROOT_ERROR_TITLE);
}

/**
 * Body of the root error overlay.
 *
 * `root_error_message` is not in any bundle yet — it arrives with a separate
 * localization PR — and `Locale.getString` answers `""` for a key it does not
 * have. Falling back to the generic "Something went wrong. Please try again."
 * is what keeps the overlay from rendering a blank body until then, and the
 * fallback stops applying by itself once the string lands.
 *
 * The deferral is registered in `localeKeyCoverage.test.ts`
 * (`KNOWN_MISSING_EN_KEYS`); that allowlist has an anti-rot check, so the entry
 * has to come out in the same PR that adds the string.
 */
export function getAppLoadErrorMessage(): string {
  const message: string = Locale.getString(LocaleKeys.ROOT_ERROR_MESSAGE);
  if (message.length > 0) {
    return message;
  }
  return Locale.getString(LocaleKeys.COMMON_ERROR_UNKNOWN);
}

/**
 * Reload + Exit (TV only) actions for the root error overlay.
 *
 * Reload `keepsOverlayOpen` so the overlay stays on screen, with the button in
 * its loading state, for the moment between the press and the page going away.
 */
export function buildAppLoadErrorActions(): AlertOverlayAction[] {
  const actions: AlertOverlayAction[] = [];
  // VegaOS is checked by its injected platform flag rather than left to the
  // user-agent device-type sniff: the app runs there inside a
  // ReactNativeWebView whose UA need not carry a TV marker.
  if (DeviceModel.isTvDevice() || DeviceModel.isVegOsPlatform()) {
    actions.push({
      id: APP_LOAD_ERROR_EXIT_ACTION_ID,
      label: Locale.getString(LocaleKeys.ACTION_EXIT)
    });
  }
  actions.push({
    id: APP_LOAD_ERROR_RELOAD_ACTION_ID,
    label: Locale.getString(LocaleKeys.ACTION_RETRY),
    keepsOverlayOpen: true
  });
  return actions;
}

/**
 * Support-facing code shown on the SOS overlay, so a report of it can be
 * matched to this specific failure mode (the session's silent-restart budget
 * ran out) rather than a generic "something crashed."
 */
export const SOS_ERROR_CODE: string = "H111";

/** Title of the SOS overlay. */
export function getSosErrorTitle(): string {
  return Locale.getString(LocaleKeys.SOS_ERROR_TITLE);
}

/** Body of the SOS overlay, with the support code appended. */
export function getSosErrorMessage(): string {
  return `${Locale.getString(LocaleKeys.SOS_ERROR_MESSAGE)} (${SOS_ERROR_CODE})`;
}

/**
 * The SOS overlay's one action: Exit, on every platform (including browser,
 * where {@link DeviceModel.closeApplication} is a no-op) — by design, there is
 * no Retry here. The session already spent its restart budget, so another
 * silent restart is not on the table; only a full app exit (and a fresh
 * budget on the next launch) is offered.
 */
export function buildSosErrorActions(): AlertOverlayAction[] {
  return [
    {
      id: APP_LOAD_ERROR_EXIT_ACTION_ID,
      label: Locale.getString(LocaleKeys.ACTION_EXIT)
    }
  ];
}
